import React from 'react';
import { BRAND, SUPPORT_EMAIL, HAS_TELEGRAM } from '../../../config/region';

/*
 * "novel-creation-guide" in every language it has been translated into.
 *
 * Shared fields (slug, dates) sit at the top level; each language supplies only what differs.
 * Russian is the authored original. A language that is missing here falls back to English, or to
 * Russian in the russia build — see blogPosts/index.js.
 */
const post = {
  slug: 'novel-creation-guide',
  datePublished: '2025-06-01',
  dateModified: '2026-02-01',

  russian: {
    title: '🛠 Инструкция создания новелл',
    description: `Подробное руководство по созданию новелл в конструкторе ${BRAND}: ресурсы, персонажи, сцены, состояния.`,
    seoTitle: `Как создать новеллу — Инструкция ${BRAND}`,
    seoDescription: `Подробная инструкция по созданию интерактивных новелл в конструкторе ${BRAND}: загрузка ресурсов, создание персонажей, статические и интерактивные сцены, концовки.`,
    content: (
      <>
        {/* Telegram only exists for {BRAND} — HAS_TELEGRAM is false in europe, so plotex.io/ru
            points readers at the support mailbox instead. */}
        <p>
          Подробная инструкция по созданию новелл в конструкторе {BRAND}. По всем
          вопросам пишите {HAS_TELEGRAM ? (
            <>в Telegram:{' '}
              <a href="https://t.me/novelladmin" target="_blank" rel="noopener noreferrer">
                @novelladmin
              </a>
            </>
          ) : (
            <>на <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a></>
          )}
        </p>

        <h2>Порядок действий</h2>

        <h3>1. Спрайты и фоны (раздел «Ресурсы»)</h3>
        <p>
          Создаём спрайты (картинки персонажей) и фоны с помощью любого сервиса
          генерации изображений.
        </p>
        <p>
          <strong>Важно:</strong> картинки должны быть одинакового размера
          (например, 1090×1690).
        </p>

        <h3>2. Персонажи (раздел «Персонажи»)</h3>
        <p>
          Добавляем персонажей: картинки и биографии. Размер описания персонажа —
          не больше 750 символов.
        </p>
        <p><strong>Пример описания:</strong></p>
        <blockquote>
          Иннокентий, 20 лет, сосед в общаге<br />
          <em>#биография</em> — Родился и вырос в Питере. Учёба не является приоритетом,
          часто пропускает пары. Не работает.<br />
          <em>#характер</em> — Меланхолик, философ, псевдоинтеллектуал, коммунист.
          Склонен к самоанализу. Ненавидит спорт, любит рок и книги.<br />
          <em>#внешность</em> — Рост около 185 см, крепкое телосложение, тёмно-русые
          волосы, короткая борода.
        </blockquote>

        <h3>3. Промпт мира</h3>
        <p>
          Контекстные описания, которые влияют на ответы персонажей при выполнении
          условий — правила, которые задействуются при определённых условиях.
        </p>

        <h3>4. Статические сцены</h3>
        <p>
          Сцены, в которых игрок ничего не пишет — ему только выводится текст.
        </p>
        <p>
          <strong>Обязательно:</strong> название, сообщение, фон, говорящий и
          следующая сцена.
        </p>

        <h3>5. Интерактивные сцены</h3>
        <p>
          Сцены, в которых игрок взаимодействует с персонажами. Обязательно нужно
          добавить фон, персонажей, стартовое сообщение и концовку.
        </p>
        <p>
          <strong>Подсказки сцены</strong> — промпты, которые предоставляют
          дополнительную информацию ИИ об этой сцене.
        </p>

        <h3>6. Концовки</h3>
        <ul>
          <li>
            <strong>Автоматические</strong> — сцена заканчивается при определённом
            условии (например, игрок попрощался).
          </li>
          <li>
            <strong>Выборы</strong> — сцена заканчивается, когда игрок выбирает
            действие (например, куда пойти).
          </li>
          <li>
            <strong>Контекстная</strong> — сцена заканчивается после определённого
            количества сообщений.
          </li>
        </ul>
        <p>В каждой концовке обязательно выставляйте следующую сцену.</p>

        <h2>Дополнительные возможности</h2>

        <h3>Музыка</h3>
        <p>Загружайте в разделе «Ресурсы», добавляйте в разделе «Музыка».</p>

        <h3>Шкалы и флаги (раздел «Состояния»)</h3>
        <p>
          <strong>Флаг</strong> — условие с двумя значениями: выполнено / не
          выполнено (True / False). Например, игроку нужно собрать осколок
          памяти — базово стоит False, а при выполнении условия становится True.
        </p>
        <p>
          <strong>Шкала</strong> — количественное измерение в числовом значении.
          Например, шкала отношений или подсчёт осколков памяти.
        </p>
        <p>
          <strong>Модификации:</strong> одиночная (меняется в конце диалога 1 раз)
          и непрерывная (меняется постоянно по ходу диалога).
        </p>
        <p>
          Внутри концовки выставляем условия, при которых она появляется. Например:
          игрок выполнил квест (флаг = True) и собрал больше 4 осколков (шкала &gt; 4).
        </p>
      </>
    ),
  },

  english: {
    title: '🛠 How to create novels',
    description: `Guide to creating novels in the ${BRAND} builder: assets, characters, scenes, and states.`,
    seoTitle: `How to create a novel — ${BRAND} Guide`,
    seoDescription: `Complete guide to creating interactive visual novels in the ${BRAND} builder: uploading assets, creating characters, static and interactive scenes, endings.`,
    content: (
      <>
        <p>
          Complete guide to creating novels in the {BRAND} builder. For any
          questions, contact us:{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </p>

        <h2>Steps to follow</h2>

        <h3>1. Sprites and backgrounds (Assets section)</h3>
        <p>
          Create sprites (character images) and backgrounds using any image
          generation service.
        </p>
        <p>
          <strong>Important:</strong> all images must be the same size
          (for example, 1090×1690).
        </p>

        <h3>2. Characters (Characters section)</h3>
        <p>
          Add characters: images and biographies. Character description size —
          no more than 750 characters.
        </p>
        <p><strong>Example description:</strong></p>
        <blockquote>
          Innocent, 20 years old, dorm neighbor<br />
          <em>#biography</em> — Born and raised in Saint Petersburg. Studies are not
          a priority, often skips classes. Does not work.<br />
          <em>#character</em> — Melancholic, philosopher, pseudo-intellectual, communist.
          Prone to self-analysis. Hates sports, loves rock and books.<br />
          <em>#appearance</em> — About 185 cm tall, athletic build, dark blonde
          hair, short beard.
        </blockquote>

        <h3>3. World Prompt</h3>
        <p>
          Context descriptions that affect character responses under certain
          conditions — rules that activate under specific circumstances.
        </p>

        <h3>4. Static Scenes</h3>
        <p>
          Scenes where the player does not write anything — they only receive text.
        </p>
        <p>
          <strong>Required:</strong> title, message, background, speaker, and
          next scene.
        </p>

        <h3>5. Interactive Scenes</h3>
        <p>
          Scenes where the player interacts with characters. You must add a
          background, characters, a starting message, and an ending.
        </p>
        <p>
          <strong>Scene hints</strong> — prompts that provide additional
          information to the AI about this scene.
        </p>

        <h3>6. Endings</h3>
        <ul>
          <li>
            <strong>Automatic</strong> — scene ends under a specific
            condition (for example, player said goodbye).
          </li>
          <li>
            <strong>Choices</strong> — scene ends when the player chooses
            an action (for example, where to go).
          </li>
          <li>
            <strong>Context-based</strong> — scene ends after a specific
            number of messages.
          </li>
        </ul>
        <p>In each ending, be sure to set the next scene.</p>

        <h2>Additional features</h2>

        <h3>Music</h3>
        <p>Upload in the Assets section, add in the Music section.</p>

        <h3>Scales and flags (States section)</h3>
        <p>
          <strong>Flag</strong> — a condition with two values: completed / not
          completed (True / False). For example, the player needs to collect a
          memory shard — it starts as False, and becomes True when the condition is met.
        </p>
        <p>
          <strong>Scale</strong> — a numerical measurement with a number value.
          For example, a relationship scale or counting memory shards.
        </p>
        <p>
          <strong>Modifications:</strong> single (changes at the end of dialogue once)
          and continuous (changes throughout the dialogue).
        </p>
        <p>
          Inside the ending, set conditions for when it appears. For example:
          the player completed a quest (flag = True) and collected more than 4 shards (scale &gt; 4).
        </p>
      </>
    ),
  },

  german: {
    title: '🛠 Anleitung zum Erstellen von Novellen',
    description: `Leitfaden zum Erstellen von Novellen im ${BRAND}-Builder: Assets, Charaktere, Szenen und Zustände.`,
    seoTitle: `So erstellst du eine Novelle — ${BRAND}-Anleitung`,
    seoDescription: `Vollständige Anleitung zum Erstellen interaktiver visueller Novellen im ${BRAND}-Builder: Assets hochladen, Charaktere erstellen, statische und interaktive Szenen, Endings.`,
    content: (
      <>
        <p>
          Vollständige Anleitung zum Erstellen von Novellen im {BRAND}-Builder.
          Bei Fragen kontaktieren Sie uns:{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </p>

        <h2>Schritte</h2>

        <h3>1. Sprites und Hintergründe (Assets-Bereich)</h3>
        <p>
          Erstelle Sprites (Charakterbilder) und Hintergründe mit einem beliebigen
          Bildgenerationsdienst.
        </p>
        <p>
          <strong>Wichtig:</strong> Alle Bilder müssen dieselbe Größe haben
          (z. B. 1090×1690).
        </p>

        <h3>2. Charaktere (Charaktere-Bereich)</h3>
        <p>
          Füge Charaktere hinzu: Bilder und Biografien. Charakterbeschreibung —
          nicht mehr als 750 Zeichen.
        </p>
        <p><strong>Beispielbeschreibung:</strong></p>
        <blockquote>
          Innocence, 20 Jahre, Nachbar im Wohnheim<br />
          <em>#biografie</em> — In Sankt Petersburg geboren und aufgewachsen. Studium ist keine
          Priorität, schwänzt oft den Unterricht. Arbeitet nicht.<br />
          <em>#charakter</em> — Melancholisch, Philosoph, Pseudo-Intellektueller, Kommunist.
          Neigt zur Selbstanalyse. Hasst Sport, mag Rock und Bücher.<br />
          <em>#aussehen</em> — Etwa 185 cm groß, sportliche Figur, dunkelblondes
          Haar, kurzer Bart.
        </blockquote>

        <h3>3. Welt-Prompt</h3>
        <p>
          Kontextbeschreibungen, die Charakterantworten unter bestimmten
          Bedingungen beeinflussen — Regeln, die unter bestimmten Umständen aktiviert werden.
        </p>

        <h3>4. Statische Szenen</h3>
        <p>
          Szenen, in denen der Spieler nichts schreibt — er erhält nur Text.
        </p>
        <p>
          <strong>Erforderlich:</strong> Titel, Nachricht, Hintergrund, Sprecher und
          nächste Szene.
        </p>

        <h3>5. Interaktive Szenen</h3>
        <p>
          Szenen, in denen der Spieler mit Charakteren interagiert. Du musst einen
          Hintergrund, Charaktere, eine Startnachricht und ein Ending hinzufügen.
        </p>
        <p>
          <strong>Szenen-Hinweise</strong> — Hinweise, die der KI zusätzliche
          Informationen über diese Szene geben.
        </p>

        <h3>6. Endings</h3>
        <ul>
          <li>
            <strong>Automatisch</strong> — Szene endet unter einer bestimmten
            Bedingung (z. B. Spieler verabschiedet sich).
          </li>
          <li>
            <strong>Wahlmöglichkeiten</strong> — Szene endet, wenn der Spieler
            eine Aktion wählt (z. B. wohin gehen).
          </li>
          <li>
            <strong>Kontextbasiert</strong> — Szene endet nach einer bestimmten
            Anzahl von Nachrichten.
          </li>
        </ul>
        <p>Lege bei jedem Ending die nächste Szene fest.</p>

        <h2>Zusätzliche Funktionen</h2>

        <h3>Musik</h3>
        <p>Im Assets-Bereich hochladen, im Musik-Bereich hinzufügen.</p>

        <h3>Skalen und Flags (Bereich Zustände)</h3>
        <p>
          <strong>Flag</strong> — eine Bedingung mit zwei Werten: erfüllt / nicht
          erfüllt (True / False). Zum Beispiel muss der Spieler einen
          Erinnerungssplitter sammeln — er beginnt als False und wird True, wenn die Bedingung erfüllt ist.
        </p>
        <p>
          <strong>Skala</strong> — eine numerische Messung mit einem Zahlenwert.
          Zum Beispiel eine Beziehungsskala oder das Zählen von Erinnerungssplittern.
        </p>
        <p>
          <strong>Modifikationen:</strong> einzeln (ändert sich am Ende des Dialogs einmal)
          und kontinuierlich (ändert sich während des Dialogs).
        </p>
        <p>
          Lege im Ending die Bedingungen fest, unter denen es angezeigt wird. Zum Beispiel:
          Der Spieler hat eine Quest erfüllt (Flag = True) und mehr als 4 Splitter gesammelt (Skala &gt; 4).
        </p>
      </>
    ),
  },

  french: {
    title: '🛠 Guide de création de romans',
    description: `Guide pour créer des romans dans l'éditeur ${BRAND} : ressources, personnages, scènes et états.`,
    seoTitle: `Comment créer un roman — Guide ${BRAND}`,
    seoDescription: `Guide complet pour créer des romans visuels interactifs dans l'éditeur ${BRAND} : télécharger des ressources, créer des personnages, scènes statiques et interactives, fins.`,
    content: (
      <>
        <p>
          Guide complet pour créer des romans dans l&apos;éditeur {BRAND}.
          Pour toute question, contactez-nous :{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </p>

        <h2>Étapes à suivre</h2>

        <h3>1. Sprites et arrière-plans (section Ressources)</h3>
        <p>
          Créez des sprites (images de personnages) et des arrière-plans à l&apos;aide de
          n&apos;importe quel service de génération d&apos;images.
        </p>
        <p>
          <strong>Important :</strong> toutes les images doivent être de la même taille
          (par exemple, 1090×1690).
        </p>

        <h3>2. Personnages (section Personnages)</h3>
        <p>
          Ajoutez des personnages : images et biographies. La taille de la description du
          personnage — pas plus de 750 caractères.
        </p>
        <p><strong>Exemple de description :</strong></p>
        <blockquote>
          Innocent, 20 ans, voisin de résidence<br />
          <em>#biographie</em> — Né et élevé à Saint-Pétersbourg. Les études ne sont pas
          une priorité, rate souvent les cours. Ne travaille pas.<br />
          <em>#caractère</em> — Mélancolique, philosophe, pseudo-intellectuel, communiste.
          Enclin à l&apos;auto-analyse. Déteste le sport, aime le rock et les livres.<br />
          <em>#apparence</em> — Environ 185 cm de haut, silhouette sportive, cheveux
          blonds foncés, petite barbe.
        </blockquote>

        <h3>3. Invite du monde</h3>
        <p>
          Descriptions contextuelles qui affectent les réponses des personnages dans
          certaines conditions — des règles qui s&apos;activent dans des circonstances spécifiques.
        </p>

        <h3>4. Scènes statiques</h3>
        <p>
          Scènes dans lesquelles le joueur n&apos;écrit rien — il reçoit seulement du texte.
        </p>
        <p>
          <strong>Requis :</strong> titre, message, arrière-plan, locuteur et
          scène suivante.
        </p>

        <h3>5. Scènes interactives</h3>
        <p>
          Scènes dans lesquelles le joueur interagit avec les personnages. Vous devez ajouter un
          arrière-plan, des personnages, un message de démarrage et une fin.
        </p>
        <p>
          <strong>Indices de scène</strong> — des indications qui fournissent des informations
          supplémentaires à l&apos;IA sur cette scène.
        </p>

        <h3>6. Fins</h3>
        <ul>
          <li>
            <strong>Automatique</strong> — la scène se termine sous une condition
            spécifique (par exemple, le joueur a dit au revoir).
          </li>
          <li>
            <strong>Choix</strong> — la scène se termine quand le joueur choisit
            une action (par exemple, où aller).
          </li>
          <li>
            <strong>Basée sur le contexte</strong> — la scène se termine après un
            nombre spécifique de messages.
          </li>
        </ul>
        <p>Dans chaque fin, assurez-vous de définir la scène suivante.</p>

        <h2>Fonctionnalités supplémentaires</h2>

        <h3>Musique</h3>
        <p>Téléchargez dans la section Ressources, ajoutez dans la section Musique.</p>

        <h3>Échelles et indicateurs (section États)</h3>
        <p>
          <strong>Indicateur</strong> — une condition avec deux valeurs : complété / non
          complété (True / False). Par exemple, le joueur doit collecter un fragment
          de mémoire — il commence à False et devient True lorsque la condition est remplie.
        </p>
        <p>
          <strong>Échelle</strong> — une mesure numérique avec une valeur numérique.
          Par exemple, une échelle de relation ou un décompte des fragments de mémoire.
        </p>
        <p>
          <strong>Modifications :</strong> simple (change à la fin du dialogue une fois)
          et continue (change tout au long du dialogue).
        </p>
        <p>
          À l&apos;intérieur de la fin, définissez les conditions d&apos;apparition. Par exemple :
          le joueur a complété une quête (indicateur = True) et collecté plus de 4 fragments (échelle &gt; 4).
        </p>
      </>
    ),
  },

  spanish: {
    title: '🛠 Guía de creación de novelas',
    description: `Guía para crear novelas en el constructor ${BRAND}: recursos, personajes, escenas y estados.`,
    seoTitle: `Cómo crear una novela — Guía ${BRAND}`,
    seoDescription: `Guía completa para crear novelas visuales interactivas en el constructor ${BRAND}: subir recursos, crear personajes, escenas estáticas e interactivas, finales.`,
    content: (
      <>
        <p>
          Guía completa para crear novelas en el constructor {BRAND}. Para
          cualquier pregunta, contáctenos:{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </p>

        <h2>Pasos a seguir</h2>

        <h3>1. Sprites y fondos (sección Recursos)</h3>
        <p>
          Crea sprites (imágenes de personajes) y fondos usando cualquier servicio
          de generación de imágenes.
        </p>
        <p>
          <strong>Importante:</strong> todas las imágenes deben tener el mismo tamaño
          (por ejemplo, 1090×1690).
        </p>

        <h3>2. Personajes (sección Personajes)</h3>
        <p>
          Agrega personajes: imágenes y biografías. Tamaño de la descripción del
          personaje — no más de 750 caracteres.
        </p>
        <p><strong>Ejemplo de descripción:</strong></p>
        <blockquote>
          Inocencio, 20 años, vecino de residencia<br />
          <em>#biografía</em> — Nacido y criado en San Petersburgo. Los estudios no son
          una prioridad, a menudo falta a clases. No trabaja.<br />
          <em>#carácter</em> — Melancólico, filósofo, pseudointelectual, comunista.
          Propenso al autoanálisis. Odia el deporte, ama el rock y los libros.<br />
          <em>#apariencia</em> — Aproximadamente 185 cm de altura, complexión atlética,
          cabello rubio oscuro, barba corta.
        </blockquote>

        <h3>3. Indicación del mundo</h3>
        <p>
          Descripciones contextuales que afectan las respuestas de los personajes en
          ciertas condiciones — reglas que se activan bajo circunstancias específicas.
        </p>

        <h3>4. Escenas estáticas</h3>
        <p>
          Escenas donde el jugador no escribe nada — solo recibe texto.
        </p>
        <p>
          <strong>Requerido:</strong> título, mensaje, fondo, orador y
          siguiente escena.
        </p>

        <h3>5. Escenas interactivas</h3>
        <p>
          Escenas donde el jugador interactúa con los personajes. Debes agregar un
          fondo, personajes, un mensaje inicial y un final.
        </p>
        <p>
          <strong>Pistas de escena</strong> — indicaciones que proporcionan
          información adicional a la IA sobre esta escena.
        </p>

        <h3>6. Finales</h3>
        <ul>
          <li>
            <strong>Automático</strong> — la escena termina bajo una condición
            específica (por ejemplo, el jugador se despidió).
          </li>
          <li>
            <strong>Opciones</strong> — la escena termina cuando el jugador elige
            una acción (por ejemplo, a dónde ir).
          </li>
          <li>
            <strong>Basado en contexto</strong> — la escena termina después de un
            número específico de mensajes.
          </li>
        </ul>
        <p>En cada final, asegúrate de establecer la siguiente escena.</p>

        <h2>Funciones adicionales</h2>

        <h3>Música</h3>
        <p>Sube en la sección Recursos, agrega en la sección Música.</p>

        <h3>Escalas e indicadores (sección Estados)</h3>
        <p>
          <strong>Indicador</strong> — una condición con dos valores: completado / no
          completado (True / False). Por ejemplo, el jugador debe recopilar un
          fragmento de memoria — comienza como False y se convierte en True cuando se cumple la condición.
        </p>
        <p>
          <strong>Escala</strong> — una medida numérica con un valor numérico.
          Por ejemplo, una escala de relación o conteo de fragmentos de memoria.
        </p>
        <p>
          <strong>Modificaciones:</strong> única (cambia al final del diálogo una vez)
          y continua (cambia durante el diálogo).
        </p>
        <p>
          Dentro del final, establece las condiciones para que aparezca. Por ejemplo:
          el jugador completó una misión (indicador = True) y recopiló más de 4 fragmentos (escala &gt; 4).
        </p>
      </>
    ),
  },

  italian: {
    title: '🛠 Guida alla creazione di romanzi',
    description: `Guida per creare romanzi nel builder ${BRAND}: risorse, personaggi, scene e stati.`,
    seoTitle: `Come creare un romanzo — Guida ${BRAND}`,
    seoDescription: `Guida completa per creare romanzi visivi interattivi nel builder ${BRAND}: caricare risorse, creare personaggi, scene statiche e interattive, finali.`,
    content: (
      <>
        <p>
          Guida completa per creare romanzi nel builder {BRAND}.
          Per qualsiasi domanda, contattaci:{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </p>

        <h2>Passaggi da seguire</h2>

        <h3>1. Sprite e sfondi (sezione Risorse)</h3>
        <p>
          Crea sprite (immagini di personaggi) e sfondi utilizzando qualsiasi
          servizio di generazione di immagini.
        </p>
        <p>
          <strong>Importante:</strong> tutte le immagini devono essere della stessa dimensione
          (ad esempio, 1090×1690).
        </p>

        <h3>2. Personaggi (sezione Personaggi)</h3>
        <p>
          Aggiungi personaggi: immagini e biografie. Dimensione della descrizione del
          personaggio — non più di 750 caratteri.
        </p>
        <p><strong>Esempio di descrizione:</strong></p>
        <blockquote>
          Innocenzo, 20 anni, vicino di stanza<br />
          <em>#biografia</em> — Nato e cresciuto a San Pietroburgo. Gli studi non sono
          una priorità, spesso salta le lezioni. Non lavora.<br />
          <em>#carattere</em> — Malinconico, filosofo, pseudo-intellettuale, comunista.
          Incline all&apos;auto-analisi. Odia lo sport, ama il rock e i libri.<br />
          <em>#aspetto</em> — Alto circa 185 cm, corporatura atletica, capelli
          biondo scuro, barba corta.
        </blockquote>

        <h3>3. Indicazione del mondo</h3>
        <p>
          Descrizioni contestuali che influenzano le risposte dei personaggi in
          determinate condizioni — regole che si attivano in circostanze specifiche.
        </p>

        <h3>4. Scene statiche</h3>
        <p>
          Scene in cui il giocatore non scrive nulla — riceve solo testo.
        </p>
        <p>
          <strong>Obbligatorio:</strong> titolo, messaggio, sfondo, oratore e
          scena successiva.
        </p>

        <h3>5. Scene interattive</h3>
        <p>
          Scene in cui il giocatore interagisce con i personaggi. Devi aggiungere uno
          sfondo, personaggi, un messaggio iniziale e un finale.
        </p>
        <p>
          <strong>Suggerimenti di scena</strong> — indicazioni che forniscono
          informazioni aggiuntive all&apos;IA su questa scena.
        </p>

        <h3>6. Finali</h3>
        <ul>
          <li>
            <strong>Automatico</strong> — la scena termina in una condizione
            specifica (ad esempio, il giocatore ha detto addio).
          </li>
          <li>
            <strong>Scelte</strong> — la scena termina quando il giocatore sceglie
            un&apos;azione (ad esempio, dove andare).
          </li>
          <li>
            <strong>Basato sul contesto</strong> — la scena termina dopo un
            numero specifico di messaggi.
          </li>
        </ul>
        <p>In ogni finale, assicurati di impostare la scena successiva.</p>

        <h2>Funzioni aggiuntive</h2>

        <h3>Musica</h3>
        <p>Carica nella sezione Risorse, aggiungi nella sezione Musica.</p>

        <h3>Scale e flag (sezione Stati)</h3>
        <p>
          <strong>Flag</strong> — una condizione con due valori: completato / non
          completato (True / False). Ad esempio, il giocatore deve raccogliere un
          frammento di memoria — inizia come False e diventa True quando la condizione è soddisfatta.
        </p>
        <p>
          <strong>Scala</strong> — una misura numerica con un valore numerico.
          Ad esempio, una scala di relazione o il conteggio dei frammenti di memoria.
        </p>
        <p>
          <strong>Modificazioni:</strong> singola (cambia alla fine del dialogo una volta)
          e continua (cambia durante il dialogo).
        </p>
        <p>
          All&apos;interno del finale, imposta le condizioni per quando appare. Ad esempio:
          il giocatore ha completato una missione (flag = True) e raccolto più di 4 frammenti (scala &gt; 4).
        </p>
      </>
    ),
  },

  polish: {
    title: '🛠 Poradnik tworzenia powieści',
    description: `Przewodnik po tworzeniu powieści w konstruktorze ${BRAND}: zasoby, postacie, sceny i stany.`,
    seoTitle: `Jak stworzyć powieść — Poradnik ${BRAND}`,
    seoDescription: `Kompletny przewodnik po tworzeniu interaktywnych powieści wizualnych w konstruktorze ${BRAND}: przesyłanie zasobów, tworzenie postaci, sceny statyczne i interaktywne, finały.`,
    content: (
      <>
        <p>
          Kompletny przewodnik po tworzeniu powieści w konstruktorze {BRAND}.
          W przypadku pytań skontaktuj się z nami:{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </p>

        <h2>Kroki do wykonania</h2>

        <h3>1. Sprite i tła (sekcja Zasoby)</h3>
        <p>
          Utwórz sprite (obrazy postaci) i tła za pomocą dowolnej usługi
          generowania obrazów.
        </p>
        <p>
          <strong>Ważne:</strong> wszystkie obrazy muszą mieć ten sam rozmiar
          (na przykład 1090×1690).
        </p>

        <h3>2. Postacie (sekcja Postacie)</h3>
        <p>
          Dodaj postacie: obrazy i biografie. Wielkość opisu postaci —
          nie więcej niż 750 znaków.
        </p>
        <p><strong>Przykładowy opis:</strong></p>
        <blockquote>
          Innocenty, 20 lat, sąsiad akademika<br />
          <em>#biografia</em> — Urodzony i wychowany w Petersburgu. Nauka nie jest
          priorytetem, często opuszcza zajęcia. Nie pracuje.<br />
          <em>#charakter</em> — Melancholik, filozof, pseudo-intelektualista, komunista.
          Skłonny do samoanalizy. Nienawidzi sportu, kocha rock i książki.<br />
          <em>#wygląd</em> — Wysoki około 185 cm, atletyczna budowa, ciemny blond
          włosy, krótka broda.
        </blockquote>

        <h3>3. Podpowiedź świata</h3>
        <p>
          Opisy kontekstowe, które wpływają na odpowiedzi postaci w
          określonych warunkach — zasady, które aktywują się w konkretnych okolicznościach.
        </p>

        <h3>4. Sceny statyczne</h3>
        <p>
          Sceny, w których gracz nic nie pisze — otrzymuje tylko tekst.
        </p>
        <p>
          <strong>Wymagane:</strong> tytuł, wiadomość, tło, mówca i
          następna scena.
        </p>

        <h3>5. Sceny interaktywne</h3>
        <p>
          Sceny, w których gracz wchodzi w interakcję z postaciami. Musisz dodać
          tło, postacie, wiadomość startową i zakończenie.
        </p>
        <p>
          <strong>Wskazówki sceny</strong> — wskazówki, które dostarczają
          dodatkowych informacji SI o tej scenie.
        </p>

        <h3>6. Zakończenia</h3>
        <ul>
          <li>
            <strong>Automatyczne</strong> — scena kończy się w określonym
            warunku (na przykład gracz się pożegnał).
          </li>
          <li>
            <strong>Wybory</strong> — scena kończy się, gdy gracz wybiera
            działanie (na przykład dokąd iść).
          </li>
          <li>
            <strong>Oparte na kontekście</strong> — scena kończy się po
            określonej liczbie wiadomości.
          </li>
        </ul>
        <p>W każdym zakończeniu upewnij się, że ustawiasz następną scenę.</p>

        <h2>Dodatkowe funkcje</h2>

        <h3>Muzyka</h3>
        <p>Prześlij w sekcji Zasoby, dodaj w sekcji Muzyka.</p>

        <h3>Skale i flagi (sekcja Stany)</h3>
        <p>
          <strong>Flaga</strong> — warunek z dwiema wartościami: ukończone / nie
          ukończone (True / False). Na przykład gracz musi zebrać
          fragment pamięci — zaczyna się jako False i staje się True, gdy warunek jest spełniony.
        </p>
        <p>
          <strong>Skala</strong> — pomiar numeryczny z wartością liczbową.
          Na przykład skala relacji lub liczenie fragmentów pamięci.
        </p>
        <p>
          <strong>Modyfikacje:</strong> pojedyncza (zmienia się na koniec dialogu raz)
          i ciągła (zmienia się w trakcie dialogu).
        </p>
        <p>
          Wewnątrz zakończenia ustaw warunki, kiedy się pojawia. Na przykład:
          gracz ukończył zadanie (flaga = True) i zebrał więcej niż 4 fragmenty (skala &gt; 4).
        </p>
      </>
    ),
  },

  portuguese: {
    title: '🛠 Como criar novels',
    description: `Guia para criar novels no editor do ${BRAND}: recursos, personagens, cenas e estados.`,
    seoTitle: `Como criar uma novel — Guia do ${BRAND}`,
    seoDescription: `Guia completo para criar visual novels interativas no editor do ${BRAND}: envio de recursos, criação de personagens, cenas estáticas e interativas, finais.`,
    content: (
      <>
        <p>
          Guia completo para criar novels no editor do {BRAND}. Se tiver
          alguma dúvida, fale com a gente:{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </p>

        <h2>Passo a passo</h2>

        <h3>1. Sprites e cenários (seção Recursos)</h3>
        <p>
          Crie sprites (imagens dos personagens) e cenários usando qualquer
          serviço de geração de imagens.
        </p>
        <p>
          <strong>Importante:</strong> todas as imagens devem ter o mesmo tamanho
          (por exemplo, 1090×1690).
        </p>

        <h3>2. Personagens (seção Personagens)</h3>
        <p>
          Adicione personagens: imagens e biografias. A descrição do personagem
          deve ter no máximo 750 caracteres.
        </p>
        <p><strong>Exemplo de descrição:</strong></p>
        <blockquote>
          Inocêncio, 20 anos, vizinho de dormitório<br />
          <em>#biografia</em> — Nasceu e cresceu em São Petersburgo. Os estudos não
          são prioridade, costuma faltar às aulas. Não trabalha.<br />
          <em>#personalidade</em> — Melancólico, filósofo, pseudointelectual, comunista.
          Tende à autoanálise. Odeia esportes, adora rock e livros.<br />
          <em>#aparência</em> — Cerca de 1,85 m de altura, porte atlético, cabelo
          loiro-escuro, barba curta.
        </blockquote>

        <h3>3. Prompt do mundo</h3>
        <p>
          Descrições de contexto que influenciam as respostas dos personagens sob
          certas condições — regras que são ativadas em circunstâncias específicas.
        </p>

        <h3>4. Cenas estáticas</h3>
        <p>
          Cenas em que o jogador não escreve nada — ele apenas recebe o texto.
        </p>
        <p>
          <strong>Obrigatório:</strong> título, mensagem, cenário, narrador e
          próxima cena.
        </p>

        <h3>5. Cenas interativas</h3>
        <p>
          Cenas em que o jogador interage com os personagens. É preciso adicionar
          um cenário, personagens, uma mensagem inicial e um final.
        </p>
        <p>
          <strong>Dicas da cena</strong> — prompts que fornecem informações
          adicionais à IA sobre essa cena.
        </p>

        <h3>6. Finais</h3>
        <ul>
          <li>
            <strong>Automáticos</strong> — a cena termina sob uma condição
            específica (por exemplo, o jogador se despediu).
          </li>
          <li>
            <strong>Escolhas</strong> — a cena termina quando o jogador escolhe
            uma ação (por exemplo, para onde ir).
          </li>
          <li>
            <strong>Por contexto</strong> — a cena termina depois de um
            número específico de mensagens.
          </li>
        </ul>
        <p>Em cada final, não se esqueça de definir a próxima cena.</p>

        <h2>Recursos adicionais</h2>

        <h3>Música</h3>
        <p>Envie na seção Recursos e adicione na seção Música.</p>

        <h3>Escalas e flags (seção Estados)</h3>
        <p>
          <strong>Flag</strong> — uma condição com dois valores: cumprida / não
          cumprida (True / False). Por exemplo, o jogador precisa coletar um
          fragmento de memória — começa como False e vira True quando a condição é cumprida.
        </p>
        <p>
          <strong>Escala</strong> — uma medida numérica com um valor.
          Por exemplo, uma escala de relacionamento ou a contagem de fragmentos de memória.
        </p>
        <p>
          <strong>Modificações:</strong> únicas (mudam uma vez, no fim do diálogo)
          e contínuas (mudam ao longo do diálogo).
        </p>
        <p>
          Dentro do final, defina as condições para que ele apareça. Por exemplo:
          o jogador concluiu uma missão (flag = True) e coletou mais de 4 fragmentos (escala &gt; 4).
        </p>
      </>
    ),
  },

};

export default post;
